import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FiMinus, FiPlus, FiSearch, FiShoppingBag } from 'react-icons/fi';
import logoPadrao from '../../assets/logo.png';
import { buscarCardapioPublico } from '../../api/cardapioPublico';
import { fazerPedidoPublico, PedidoPublicoIdentificacao } from '../../api/pedidos';
import { IdentificacaoClienteModal } from '../../components/IdentificacaoClienteModal';
import { CardapioPublico as CardapioPublicoTipo } from '../../types';
import { formatarMoeda } from '../../utils/formatadores';

type Carrinho = Record<number, number>;

interface ClienteSalvo {
  nome: string;
  telefone: string;
}

function chaveClienteSalvo(tenantId: string) {
  return `lizzi_food_cliente_${tenantId}`;
}

function lerClienteSalvo(tenantId: string): ClienteSalvo | null {
  try {
    const bruto = localStorage.getItem(chaveClienteSalvo(tenantId));
    return bruto ? (JSON.parse(bruto) as ClienteSalvo) : null;
  } catch {
    return null;
  }
}

export function CardapioPublico() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [cardapio, setCardapio] = useState<CardapioPublicoTipo | null>(null);
  const [erro, setErro] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [categoriaAtiva, setCategoriaAtiva] = useState<number | 'all' | 'outros'>('all');
  const [carrinho, setCarrinho] = useState<Carrinho>({});
  const [enviando, setEnviando] = useState(false);
  const [pedidoConfirmado, setPedidoConfirmado] = useState<{ id: number; total: number } | null>(null);
  const [mostrarIdentificacao, setMostrarIdentificacao] = useState(false);
  const clienteSalvo = tenantId ? lerClienteSalvo(tenantId) : null;

  useEffect(() => {
    if (!tenantId) return;
    buscarCardapioPublico(tenantId)
      .then((resposta) => {
        setCardapio(resposta);
        const primeiraCategoria = resposta.categorias.find((categoria) => categoria.produtos.length > 0);
        setCategoriaAtiva(primeiraCategoria?.id ?? (primeiraCategoria ? 'outros' : 'all'));
      })
      .catch(() => setErro(true))
      .finally(() => setCarregando(false));
  }, [tenantId]);

  const categoriasComProdutos = useMemo(() => {
    if (!cardapio) return [];
    const termo = busca.trim().toLowerCase();

    return cardapio.categorias
      .map((categoria) => ({
        ...categoria,
        produtos: categoria.produtos.filter((produto) => {
          if (!termo) return true;
          const alvo = `${produto.nome} ${produto.descricao || ''}`.toLowerCase();
          return alvo.includes(termo);
        }),
      }))
      .filter((categoria) => categoria.produtos.length > 0);
  }, [busca, cardapio]);

  const categoriasFiltradas = useMemo(() => {
    if (categoriaAtiva === 'all') return categoriasComProdutos;
    return categoriasComProdutos.filter((categoria) => (categoria.id === null ? 'outros' : categoria.id) === categoriaAtiva);
  }, [categoriaAtiva, categoriasComProdutos]);

  const produtosPorId = useMemo(() => {
    const mapa: Record<number, { nome: string; preco_venda: number }> = {};
    categoriasComProdutos.forEach((categoria) => {
      categoria.produtos.forEach((produto) => {
        mapa[produto.id] = produto;
      });
    });
    return mapa;
  }, [categoriasComProdutos]);

  const totalItens = Object.values(carrinho).reduce((soma, qtd) => soma + qtd, 0);
  const totalPreco = Object.entries(carrinho).reduce(
    (soma, [produtoId, qtd]) => soma + (produtosPorId[Number(produtoId)]?.preco_venda || 0) * qtd,
    0
  );

  function alterarQuantidade(produtoId: number, delta: number) {
    setCarrinho((atual) => {
      const quantidadeAtual = atual[produtoId] || 0;
      const novaQuantidade = Math.max(0, quantidadeAtual + delta);

      if (novaQuantidade === 0) {
        const { [produtoId]: removido, ...restante } = atual;
        return restante;
      }

      return { ...atual, [produtoId]: novaQuantidade };
    });
  }

  function handleFazerPedido() {
    if (!tenantId || totalItens === 0) return;
    setMostrarIdentificacao(true);
  }

  async function handleConfirmarIdentificacao(identificacao: PedidoPublicoIdentificacao) {
    if (!tenantId) return;

    const itens = Object.entries(carrinho).map(([produtoId, quantidade]) => ({
      produto_id: Number(produtoId),
      quantidade,
    }));

    setEnviando(true);
    try {
      const pedido = await fazerPedidoPublico(tenantId, identificacao, itens);
      localStorage.setItem(
        chaveClienteSalvo(tenantId),
        JSON.stringify({ nome: identificacao.nome, telefone: identificacao.telefone })
      );
      setPedidoConfirmado(pedido);
      setCarrinho({});
      setMostrarIdentificacao(false);
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Nao foi possivel enviar o pedido',
        text: error.response?.data?.erro || 'Tente novamente em instantes.',
      });
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) {
    return <div className="lizzi-public-wrap" />;
  }

  if (erro || !cardapio) {
    return (
      <div className="lizzi-public-empty-screen">
        <p>Cardapio nao encontrado.</p>
      </div>
    );
  }

  if (pedidoConfirmado) {
    return (
      <div className="lizzi-public-empty-screen">
        <div className="lizzi-public-success">
          <img src={cardapio.empresa.logo_url || logoPadrao} alt={cardapio.empresa.nome} />
          <h1>Pedido enviado</h1>
          <p>
            Pedido #{pedidoConfirmado.id} confirmado. Total: <strong>{formatarMoeda(pedidoConfirmado.total)}</strong>
          </p>
          <button type="button" className="lizzi-public-primary-button" onClick={() => setPedidoConfirmado(null)}>
            Fazer novo pedido
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lizzi-public-wrap">
      <div className="lizzi-public-shell">
        <section className="lizzi-public-main">
          <header className="lizzi-public-header">
            <div className="lizzi-public-brand">
              <img src={cardapio.empresa.logo_url || logoPadrao} alt={cardapio.empresa.nome} />
              <div>
                <strong>Cardápio</strong>
                <span>Escolha seus itens</span>
              </div>
            </div>

            <label className="lizzi-public-search">
              <FiSearch size={16} />
              <input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar produto"
              />
            </label>
          </header>

          <div className="lizzi-public-category-strip">
            <button
              type="button"
              className={categoriaAtiva === 'all' ? 'ativo' : ''}
              onClick={() => setCategoriaAtiva('all')}
            >
              Todos
            </button>
            {categoriasComProdutos.map((categoria) => (
              <button
                key={categoria.id ?? 'sem-categoria'}
                type="button"
                className={categoriaAtiva === (categoria.id === null ? 'outros' : categoria.id) ? 'ativo' : ''}
                onClick={() => setCategoriaAtiva(categoria.id === null ? 'outros' : categoria.id)}
              >
                {categoria.nome}
              </button>
            ))}
          </div>

          {categoriasFiltradas.length === 0 ? (
            <div className="lizzi-empty-state">
              <span>Nenhum produto encontrado.</span>
            </div>
          ) : (
            categoriasFiltradas.map((categoria) => (
              <section key={categoria.id ?? 'sem-categoria'} className="lizzi-public-category-section">
                <div className="lizzi-public-category-header">
                  <div>
                    <h2>{categoria.nome}</h2>
                    <p>{categoria.produtos.length} itens</p>
                  </div>
                  {categoria.imagem ? (
                    <div className="lizzi-public-category-image">
                      <img src={categoria.imagem} alt={categoria.nome} />
                    </div>
                  ) : null}
                </div>

                <div className="lizzi-public-product-grid">
                  {categoria.produtos.map((produto) => (
                    <article key={produto.id} className="lizzi-public-product-card">
                      <div className="lizzi-public-product-media">
                        {produto.imagem ? <img src={produto.imagem} alt={produto.nome} /> : <span>{produto.nome.slice(0, 1)}</span>}
                      </div>
                      <div className="lizzi-public-product-copy">
                        <strong>{produto.nome}</strong>
                        {produto.descricao ? <p>{produto.descricao}</p> : <p>Sem descricao</p>}
                        <div className="lizzi-public-product-footer">
                          <span>{formatarMoeda(produto.preco_venda)}</span>
                          <div className="lizzi-public-qty">
                            <button type="button" onClick={() => alterarQuantidade(produto.id, -1)} disabled={!carrinho[produto.id]}>
                              <FiMinus size={14} />
                            </button>
                            <strong>{carrinho[produto.id] || 0}</strong>
                            <button type="button" onClick={() => alterarQuantidade(produto.id, 1)}>
                              <FiPlus size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))
          )}
        </section>

        <aside className="lizzi-public-cart">
          <div className="lizzi-public-cart-card">
            <div className="lizzi-public-cart-header">
              <FiShoppingBag size={18} />
              <div>
                <strong>Seu pedido</strong>
                <span>{totalItens} {totalItens === 1 ? 'item' : 'itens'}</span>
              </div>
            </div>

            {totalItens === 0 ? (
              <p className="lizzi-public-cart-empty">Adicione produtos para montar o pedido.</p>
            ) : (
              <>
                <div className="lizzi-public-cart-lines">
                  {Object.entries(carrinho).map(([produtoId, quantidade]) => {
                    const produto = produtosPorId[Number(produtoId)];
                    if (!produto) return null;
                    return (
                      <div key={produtoId} className="lizzi-public-cart-line">
                        <div>
                          <strong>{produto.nome}</strong>
                          <span>{quantidade} x {formatarMoeda(produto.preco_venda)}</span>
                        </div>
                        <b>{formatarMoeda(produto.preco_venda * quantidade)}</b>
                      </div>
                    );
                  })}
                </div>

                <div className="lizzi-public-cart-total">
                  <span>Total</span>
                  <strong>{formatarMoeda(totalPreco)}</strong>
                </div>
              </>
            )}

            <button
              type="button"
              className="lizzi-public-primary-button"
              onClick={handleFazerPedido}
              disabled={enviando || totalItens === 0}
            >
              {enviando ? 'Enviando...' : 'Fazer pedido'}
            </button>
          </div>
        </aside>
      </div>

      {totalItens > 0 ? (
        <div className="lizzi-public-cart-mobile">
          <span>
            {totalItens} itens • {formatarMoeda(totalPreco)}
          </span>
          <button type="button" onClick={handleFazerPedido} disabled={enviando}>
            {enviando ? 'Enviando...' : 'Pedir'}
          </button>
        </div>
      ) : null}

      {tenantId && (
        <IdentificacaoClienteModal
          show={mostrarIdentificacao}
          tenantId={tenantId}
          telefoneSalvo={clienteSalvo?.telefone}
          enviando={enviando}
          onConfirm={handleConfirmarIdentificacao}
          onHide={() => setMostrarIdentificacao(false)}
        />
      )}
    </div>
  );
}
